/**
 * Two small pure pieces around an offering's teacherName: does the free-text
 * string name this user (teacherNameMatchesUser), and what should a save
 * write to it so an edit never silently erases it (teacherNameForSave).
 *
 * Both seed pipelines (netlify/functions/seedFallSchedule.mjs, for the
 * comp-dance rows and the class rows) write teacherUid: null and put the
 * human name straight from Kim's printed schedule into teacherName instead -
 * 'Ms. Kim', 'Miss Dallis + Miss Mia + Miss Aeva', 'Ms. Kim + Staff', 'Coach
 * Carson + Ms. Kim'. Until Kim (or a Director) links the real account,
 * teacherUid is null on the offering and there is no unambiguous way to say
 * whose class it is - this is the fallback that lets Teach.tsx and
 * Offerings.tsx still show something a teacher (or Kim, assigning accounts)
 * recognizes.
 *
 * Callers MUST prefer an explicit teacherUid match and reach for this only
 * when teacherUid is null/absent. A class explicitly assigned to someone
 * else must never leak into a different teacher's list through a name
 * coincidence, and this module has no way to know about that rule itself -
 * enforcing it is the caller's job (see Teach.tsx's `mine`).
 *
 * Zero imports on purpose, the same choice lib/alternates.ts documents on
 * itself: this file needs to import cleanly under plain
 * `node --experimental-strip-types`, which offerings.ts itself cannot do -
 * proven by trying it, not assumed. offerings.ts pulls in firebase/react and
 * uses extensionless relative imports ('./storageUpload') that a bundler
 * resolves but plain Node's ESM loader refuses with ERR_MODULE_NOT_FOUND.
 * Keeping the matcher here, with nothing to resolve, is what makes it
 * directly unit-testable at all.
 */

/** Honorifics the printed schedule uses, with the trailing period stripped before matching. */
const HONORIFICS = new Set(['ms', 'miss', 'mrs', 'mr', 'coach', 'mx']);

/** Free-text tokens that are never a teacher's name, even standing alone. */
const IGNORED_TOKENS = new Set(['staff']);

function normalizeWords(value: string | null | undefined): string[] {
  if (typeof value !== 'string') return [];
  return value
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

/** Drops a leading honorific word ('ms', 'ms.', 'coach', ...) if the segment starts with one. */
function stripHonorific(words: string[]): string[] {
  if (words.length === 0) return words;
  const first = words[0].replace(/\.$/, '');
  return HONORIFICS.has(first) ? words.slice(1) : words;
}

/**
 * One normalized word list per teacher named in a teacherName string.
 * Splits on '+', '&' and ',' (the three separators the real data uses),
 * strips a leading honorific from each piece, then drops anything empty or
 * left holding only an ignored token like 'Staff'.
 */
function teacherNameCandidates(teacherName: string | null | undefined): string[][] {
  if (typeof teacherName !== 'string') return [];
  return teacherName
    .split(/[+&,]/)
    .map((segment) => stripHonorific(normalizeWords(segment)))
    .filter((words) => words.length > 0 && !(words.length === 1 && IGNORED_TOKENS.has(words[0])));
}

/**
 * Does teacherName (an offering's free-text, possibly multi-teacher field)
 * name userDisplayName? Case-insensitive, honorific-blind ON BOTH SIDES, and
 * safe against empty/null input on either side.
 *
 * Honorific stripping runs on the user's display name too, not just the
 * offering's teacherName (fix, 2026-08-05 review: 'Ms. Kim' vs 'Ms. Kim' and
 * 'Coach Carson + Ms. Kim' vs 'Coach Carson' both used to return false).
 * Every teacher string in the seed carries an honorific - it is the
 * studio's naming convention - so a staff account whose own displayName was
 * typed the same way ("Ms. Kim", not "Kim") got an empty Teach page from a
 * one-sided strip: it failed safe (no wrong leak) but silently defeated the
 * fix for exactly the users it exists to help.
 *
 * A single-word candidate ('Kim', after 'Ms. Kim' loses its honorific)
 * matches only the user's FIRST token, and only exactly - never a substring
 * or prefix, so 'Kim' does not match a first token of 'Kimberly' and 'Mia'
 * does not match 'Miami'. A multi-word candidate ('Kim Bagby') instead
 * requires the FULL normalized candidate to equal the user's full
 * normalized name - sharing just a first name is not enough once the seed
 * carries a last name too.
 *
 * Not handled: a seed segment where the honorific is not the LEADING word,
 * as in fallSchedule2026.mjs's 'Fall - Coach Maddux + Miss Peighton, Spring
 * - Coach Brock' - 'Fall - Coach Maddux' keeps all four tokens (nothing
 * leads with an honorific to strip), so it can only ever multi-word-match a
 * user literally named "Fall - Coach Maddux". Deliberately left alone: that
 * prefix is a SEASON qualifier (which of two different teachers is current
 * depends on the time of year), not a co-teacher list, and blindly matching
 * both names would show Coach Brock's spring assignment on his Teach page
 * in the fall and vice versa - a wrong-season leak, not a fix. See
 * teacher-name-match.test.mjs for the pinned case.
 *
 * Never matches on email. Pass a display name; the defensive check below
 * refuses anything that looks like one in case a caller passes email by
 * mistake.
 */
export function teacherNameMatchesUser(
  teacherName: string | null | undefined,
  userDisplayName: string | null | undefined
): boolean {
  if (typeof userDisplayName === 'string' && userDisplayName.includes('@')) return false;
  const userWords = stripHonorific(normalizeWords(userDisplayName));
  if (userWords.length === 0) return false;
  const userFirst = userWords[0];
  const userFull = userWords.join(' ');
  for (const words of teacherNameCandidates(teacherName)) {
    if (words.length === 1 ? words[0] === userFirst : words.join(' ') === userFull) {
      return true;
    }
  }
  return false;
}

/**
 * The teacherName to write to an offering on a Director's save, given the
 * teacher the form currently has selected (already resolved to a display
 * name, or null if none/unresolvable) and what the offering already has
 * stored.
 *
 * Fix, 2026-08-05 review (Offerings.tsx's handleUpdate, not this module,
 * had the bug - this is the pure decision it was missing): every seeded
 * offering starts with teacherUid: null and the printed schedule's name in
 * teacherName (seedFallSchedule.mjs), and Bobbie's live workflow right now
 * is opening those seeded classes to edit a style, level or age group
 * WITHOUT assigning a teacher yet. The old save wrote
 * `teacherName: teacherDisplayName(teachers, f.teacherUid)` unconditionally,
 * which is null whenever no teacher is selected - so any such edit silently
 * erased the printed name. After this fix's Teach.tsx/Offerings.tsx fallback
 * to teacherName, that erasure is not cosmetic: it also drops the offering
 * out of the name-matched teacher's Teach page and flips the card straight
 * to "Unassigned" with no "Not linked yet" pill to explain why - destroying
 * the exact worklist information Bobbie is using this screen for.
 *
 * The rule: only overwrite teacherName when a real teacher is actually
 * chosen. If none is chosen AND the offering never had a teacherUid either,
 * the existing teacherName (the seeded printed name, most likely) is left
 * untouched. If none is chosen but the offering DID have a teacherUid - a
 * Director explicitly clearing a real assignment - teacherName clears too,
 * on purpose: leaving a linked account's name behind after removing them
 * would read as though they still taught it.
 */
export function teacherNameForSave(
  formTeacherUid: string | null | undefined,
  candidateTeacherName: string | null,
  existingTeacherUid: string | null | undefined,
  existingTeacherName: string | null | undefined
): string | null {
  if (formTeacherUid) return candidateTeacherName;
  if (existingTeacherUid) return null;
  return existingTeacherName ?? null;
}

/**
 * One honorific everywhere, applied when the free-text name is READ.
 *
 * Bobbie, 2026-08-10: "all [teachers] need to be Ms Kim or Coach. and they
 * get to decide which." Once a teacher has an account linked, they do decide,
 * and teacherDisplayName() in lib/teacherTitle.ts renders their choice. This
 * function is for the other case, which is most of the catalog today: an
 * offering seeded from Kim's printed schedule has teacherUid null and only
 * the free text, so the screen falls back to whatever the paper said. The
 * paper says "Miss Dallis + Miss Mia + Miss Aeva", and that is what families
 * were seeing next to "Ms. Kim" on the same page.
 *
 * Why this is a READ-time formatter and not a rewrite of the seed files:
 * those strings are a transcription of a physical document, and the matcher
 * above deliberately strips honorifics before comparing (see HONORIFICS), so
 * rewriting them would risk the match keys to fix nothing. Normalising on the
 * way to the screen changes every surface at once and leaves the stored data
 * exactly as transcribed.
 *
 * Only "Miss" moves, plus a bare "Ms" gaining its missing period. **Coach is
 * never touched** - it is the other half of the choice, not a mistake - and
 * neither is "Mr.", because the rule is about Miss versus Ms., not about
 * flattening every honorific onto one. The seed data carries 88 "Ms.", 22
 * "Miss", 9 "Coach" and 1 "Mr." today, so those four are the whole problem.
 *
 * Handles the "+" lists the printed schedule uses, because the replacement is
 * per-occurrence rather than anchored to the start of the string.
 */
export function displayTeacherName(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed
    .replace(/\bMiss\.?\s+(?=[A-Za-z])/g, 'Ms. ')
    .replace(/\bMs\s+(?=[A-Za-z])/g, 'Ms. ');
}
